// ===== Shor vs Grover math for the quantum-threat capstone =====
//
// Two very different quantum algorithms, two very different consequences:
//
//  - SHOR turns factoring (and discrete log) into period finding. The function
//    f(x) = a^x mod N is periodic, and a quantum Fourier transform reads its
//    period r in one shot. From r you get the factors of N by gcd, so RSA and
//    ECC fall completely (exponential -> polynomial). The whole attack hinges
//    on that hidden periodic / abelian-group structure.
//
//  - GROVER is a generic search speedup: it finds a needle among 2^n in about
//    2^(n/2) steps. Quadratic, not exponential. It does not break RSA's
//    structure; it only halves the effective security level of symmetric
//    ciphers and hashes, which is why the fix is simply larger keys (AES-256,
//    SHA-384). Lattices have no period for a QFT to grab, so the best quantum
//    attacks are Grover-grade and lattices stay standing.
//
// Small integers throughout so it can be unit tested and animated in-browser.

// ===== integer helpers =====

export function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a;
}

/** (base^exp) mod m by repeated squaring, never overflowing for small m. */
export function modPow(base: number, exp: number, m: number): number {
  if (m === 1) return 0;
  let result = 1;
  let b = ((base % m) + m) % m;
  let e = exp;
  while (e > 0) {
    if (e & 1) result = (result * b) % m;
    e = Math.floor(e / 2);
    b = (b * b) % m;
  }
  return result;
}

/** Smallest r > 0 with a^r = 1 (mod N). This is the period Shor extracts.
 * Returns null when a and N are not coprime (then gcd already factors N). */
export function multiplicativeOrder(a: number, N: number): number | null {
  if (gcd(a, N) !== 1) return null;
  let val = 1;
  for (let r = 1; r <= N; r++) {
    val = (val * a) % N;
    if (val === 1) return r;
  }
  return null;
}

/** Split N into [smallest prime factor, cofactor]. For the demo semiprimes
 * this is the true factorization. */
export function factorPairs(N: number): [number, number] | null {
  for (let p = 2; p * p <= N; p++) {
    if (N % p === 0) return [p, N / p];
  }
  return null; // prime
}

// ===== Shor's period-finding factorization =====

export interface ShorResult {
  N: number;
  a: number;
  gcdAN: number;
  order: number | null; // the period r
  usable: boolean; // did period finding yield nontrivial factors
  factors: [number, number] | null;
  reason: string;
}

/** Run the classical post-processing of Shor's algorithm for a chosen base a.
 * The quantum part is the period r; everything here is what you do with it. */
export function shorFactor(N: number, a: number): ShorResult {
  const base = { N, a } as const;
  const g = gcd(a, N);
  if (g > 1) {
    return {
      ...base,
      gcdAN: g,
      order: null,
      usable: false,
      factors: [g, N / g],
      reason:
        'Lucky shortcut: a already shares a factor with N, so gcd splits it without the quantum step.',
    };
  }

  const r = multiplicativeOrder(a, N);
  if (r === null) {
    return {
      ...base,
      gcdAN: g,
      order: null,
      usable: false,
      factors: null,
      reason: 'No period found.',
    };
  }
  if (r % 2 !== 0) {
    return {
      ...base,
      gcdAN: g,
      order: r,
      usable: false,
      factors: null,
      reason: `Period r = ${r} is odd. Shor needs an even period; pick another base a.`,
    };
  }

  const h = modPow(a, r / 2, N);
  if (h === N - 1) {
    return {
      ...base,
      gcdAN: g,
      order: r,
      usable: false,
      factors: null,
      reason: `a^(r/2) = -1 (mod N), a dead end. Pick another base a.`,
    };
  }

  const p = gcd(h - 1, N);
  const q = gcd(h + 1, N);
  const factors: [number, number] = [p, q];
  const nontrivial = p > 1 && p < N && q > 1 && q < N;
  return {
    ...base,
    gcdAN: g,
    order: r,
    usable: nontrivial,
    factors: nontrivial ? factors : null,
    reason: nontrivial
      ? `Period r = ${r} -> a^(r/2) = ${h} -> gcd(${h}±1, ${N}) = ${p} and ${q}.`
      : 'Period gave only trivial factors; pick another base a.',
  };
}

/** Bases a in [2, N) that are coprime to N and yield nontrivial factors.
 * Lets the UI suggest a base that works. */
export function suggestBases(N: number): number[] {
  const out: number[] = [];
  for (let a = 2; a < N; a++) {
    if (gcd(a, N) === 1 && shorFactor(N, a).usable) out.push(a);
  }
  return out;
}

// ===== periodic function and the QFT view =====

/** The sequence f(x) = a^x mod N over x = 0..length-1. Periodic with period r. */
export function powerSequence(a: number, N: number, length: number): number[] {
  const out: number[] = [];
  for (let x = 0; x < length; x++) out.push(modPow(a, x, N));
  return out;
}

/** A Dirac comb: 1 at offset, offset+period, offset+2*period, ... This is the
 * post-measurement state of the input register in Shor, whose QFT spikes at
 * multiples of length/period. */
export function combSequence(period: number, length: number, offset = 0): number[] {
  const out = new Array(length).fill(0);
  for (let n = offset; n < length; n += period) out[n] = 1;
  return out;
}

/** Discrete Fourier transform magnitude spectrum. O(n^2), fine for the small
 * registers used here. This is "what the quantum Fourier transform measures." */
export function dftMagnitude(samples: number[]): number[] {
  const n = samples.length;
  const mags = new Array(n).fill(0);
  for (let k = 0; k < n; k++) {
    let re = 0;
    let im = 0;
    for (let t = 0; t < n; t++) {
      const ang = (-2 * Math.PI * k * t) / n;
      re += samples[t] * Math.cos(ang);
      im += samples[t] * Math.sin(ang);
    }
    mags[k] = Math.hypot(re, im);
  }
  return mags;
}

// ===== seeded RNG + a structureless sequence (the lattice contrast) =====

export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A random 0/1 sequence with the same number of ones as a comb of the given
 * period, so the energy matches and the only difference the spectrum can show
 * is structure. A lattice problem looks like this to a QFT: no peak. */
export function structurelessSequence(
  length: number,
  ones: number,
  rng: () => number
): number[] {
  const out = new Array(length).fill(0);
  let placed = 0;
  let guard = 0;
  while (placed < ones && guard < length * 100) {
    const idx = Math.floor(rng() * length);
    if (out[idx] === 0) {
      out[idx] = 1;
      placed++;
    }
    guard++;
  }
  return out;
}

// ===== Grover's quadratic speedup =====

export interface Primitive {
  id: string;
  label: string;
  bits: number; // classical brute-force security in bits
  note: string;
}

export const PRIMITIVES: Primitive[] = [
  { id: 'aes128', label: 'AES-128', bits: 128, note: 'Key search' },
  { id: 'aes192', label: 'AES-192', bits: 192, note: 'Key search' },
  { id: 'aes256', label: 'AES-256', bits: 256, note: 'Key search' },
  { id: 'sha256', label: 'SHA-256', bits: 256, note: 'Preimage' },
  { id: 'sha384', label: 'SHA-384', bits: 384, note: 'Preimage' },
];

export interface GroverResult {
  classicalBits: number;
  quantumBits: number; // effective bits after Grover's quadratic speedup
  safe: boolean; // >= 128-bit quantum margin
}

/** Grover halves the exponent: 2^n brute force becomes ~2^(n/2). A primitive
 * stays safe if it keeps at least a 128-bit margin afterward. */
export function groverSecurity(classicalBits: number): GroverResult {
  const quantumBits = Math.ceil(classicalBits / 2);
  return { classicalBits, quantumBits, safe: quantumBits >= 128 };
}

export const SEMIPRIMES = [15, 21, 33, 35, 39, 51, 55, 65, 77, 85, 91, 95];
